/**
 * One icon set, one weight.
 *
 * Every icon in the product is a lucide icon drawn through this component:
 * 16px with a 1.75 stroke, 14px in dense rows, 20px (1.6 stroke) for a tile.
 * The size comes from CSS in rem, so an icon grows with the text beside it.
 *
 * An icon is decoration unless it is given a `label`. Decoration is hidden
 * from assistive technology — the words next to it are the name. An icon with
 * no words next to it must have a `label`, and then it is announced as an image.
 */
import type { LucideIcon, LucideProps } from "lucide-react";
import { cx } from "./cx.js";
import "./icon.css";

export type IconSize = "xs" | "sm" | "md" | "lg";

const STROKE: Record<IconSize, number> = { xs: 2.2, sm: 1.9, md: 1.75, lg: 1.6 };

export interface IconProps extends Omit<LucideProps, "size" | "ref"> {
  /** The lucide icon to draw, e.g. `Play`. */
  as: LucideIcon;
  /** xs 12, sm 14, md 16 (default), lg 20. */
  size?: IconSize;
  /** Names the icon when nothing beside it does. Omit for decoration. */
  label?: string;
}

export function Icon({ as: Glyph, size = "md", label, className, ...rest }: IconProps) {
  return (
    <Glyph
      strokeWidth={STROKE[size]}
      {...rest}
      className={cx("mdl-icon", className)}
      data-size={size}
      focusable={false}
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    />
  );
}
